import React, { useState } from 'react';
import { BarChart, TrendingUp, Users, Eye, Heart, MessageCircle, Share, Calendar } from 'lucide-react';

export default function Analytics() {
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [selectedClient, setSelectedClient] = useState('all');

  const overviewStats = [
    { name: 'Total Reach', value: '2.4M', change: '+12.5%', changeType: 'positive', icon: Eye },
    { name: 'Engagement Rate', value: '8.2%', change: '+2.1%', changeType: 'positive', icon: Heart },
    { name: 'Total Posts', value: '156', change: '+23%', changeType: 'positive', icon: BarChart },
    { name: 'New Followers', value: '1,247', change: '+18.3%', changeType: 'positive', icon: Users },
  ];

  const platformStats = [
    { platform: 'Instagram', posts: 45, reach: '1.2M', engagement: '9.1%', followers: '+523', color: 'bg-pink-500' },
    { platform: 'Facebook', posts: 32, reach: '800K', engagement: '6.8%', followers: '+312', color: 'bg-blue-600' },
    { platform: 'LinkedIn', posts: 28, reach: '450K', engagement: '5.2%', followers: '+189', color: 'bg-blue-700' },
    { platform: 'Twitter', posts: 51, reach: '320K', engagement: '4.1%', followers: '+223', color: 'bg-sky-500' },
  ];

  const topPosts = [
    {
      id: '1',
      title: 'Product Launch Campaign',
      client: 'TechCorp',
      platform: 'Instagram',
      reach: '45.2K',
      engagement: '12.8%',
      likes: 1240,
      comments: 89,
      shares: 156,
      date: '2024-03-10'
    },
    {
      id: '2',
      title: 'Behind the Scenes Video',
      client: 'Fashion Forward',
      platform: 'TikTok',
      reach: '38.7K',
      engagement: '15.2%',
      likes: 2100,
      comments: 145,
      shares: 234,
      date: '2024-03-08'
    },
    {
      id: '3',
      title: 'Weekly Menu Special',
      client: 'Healthy Eats',
      platform: 'Facebook',
      reach: '22.1K',
      engagement: '8.9%',
      likes: 890,
      comments: 67,
      shares: 123,
      date: '2024-03-06'
    }
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Track performance across all social media platforms</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Clients</option>
            <option value="techcorp">TechCorp Solutions</option>
            <option value="fashion">Fashion Forward</option>
            <option value="healthy">Healthy Eats</option>
          </select>
          
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {overviewStats.map((stat) => (
          <div key={stat.name} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-blue-50 rounded-lg">
                <stat.icon className="h-6 w-6 text-blue-600" />
              </div>
              <span className={`text-sm font-medium ${
                stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
              }`}>
                {stat.change}
              </span>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-600">{stat.name}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Platform Performance */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Platform Performance</h3>
          <div className="space-y-4">
            {platformStats.map((platform) => (
              <div key={platform.platform} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${platform.color}`} />
                  <div>
                    <p className="font-medium text-gray-900">{platform.platform}</p>
                    <p className="text-sm text-gray-500">{platform.posts} posts</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{platform.reach}</p>
                  <p className="text-sm text-gray-500">reach</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{platform.engagement}</p>
                  <p className="text-sm text-gray-500">engagement</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-green-600">{platform.followers}</p>
                  <p className="text-sm text-gray-500">followers</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Engagement Trends */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Engagement Trends</h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Chart visualization would go here</p>
              <p className="text-sm text-gray-500">Integration with charting library needed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Performing Posts */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Top Performing Posts</h3>
          <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
            View All
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-900">Post</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Client</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Platform</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Reach</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Engagement</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Interactions</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Date</th>
              </tr>
            </thead>
            <tbody>
              {topPosts.map((post) => (
                <tr key={post.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <p className="font-medium text-gray-900">{post.title}</p>
                  </td>
                  <td className="py-4 px-4 text-gray-600">{post.client}</td>
                  <td className="py-4 px-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {post.platform}
                    </span>
                  </td>
                  <td className="py-4 px-4 font-medium text-gray-900">{post.reach}</td>
                  <td className="py-4 px-4 font-medium text-green-600">{post.engagement}</td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <Heart className="h-4 w-4 mr-1" />
                        {post.likes}
                      </span>
                      <span className="flex items-center">
                        <MessageCircle className="h-4 w-4 mr-1" />
                        {post.comments}
                      </span>
                      <span className="flex items-center">
                        <Share className="h-4 w-4 mr-1" />
                        {post.shares}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-gray-600">
                    {new Date(post.date).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}